import React, { useRef, useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  Archive,
  FolderInput,
  PlusCircle,
  ArrowRight,
  Reply,
  Undo,
  ThumbsUp,
  X,
  Maximize2,
  Wrench,
  TicketPlusIcon,
  AlertTriangle,
  Forward,
  MailOpen
} from "lucide-react";

import {
  formatActionTypeLabel,
  formatEmailText
} from "../../utils/formatter";
import {
  getActionTypeKey,
  getMetaKey,
  getReplyContentMetaKey
} from "../../utils/emailActionKeyHelper";
import { getProviderDisplayName } from "../../utils/mailProvider";
import { resolveActionDestination } from "../../utils/actionDestination";
import MaintenanceTicketDraftModal from "./MaintenanceTicketDraftModal";

const getActionConfig = (action, digest) => {
  if (!action) return {};
  switch (action.type) {
    case 'archive':
      return {
        summary: action.summary,
        icon: <Archive size={24} className="text-stone-600" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: true
      };
    case 'reply':
      return {
        summary: action.summary,
        icon: <Reply size={24} className="text-cyan-700" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: false
      };
    case 'forward':
      return {
        summary: action.summary,
        icon: <Forward size={24} className="text-sky-700" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: false
      };
    case 'markRead':
      return {
        summary: action.summary,
        icon: <MailOpen size={24} className="text-teal-600" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: false
      };
    case 'move-to-label': {
      const destination = resolveActionDestination(action, digest);
      return {
        summary: action.summary || (destination ? `Moved to ${destination.label}` : undefined),
        icon: <ArrowRight size={24} className="text-indigo-600" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: true
      };
    }
    case 'create-a-task':
      return {
        summary: action.summary,
        icon: <PlusCircle size={24} className="text-emerald-600" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: true
      };

    case 'create-a-maintenance-ticket':
      return {
        summary: action.summary,
        icon: <TicketPlusIcon size={24} className="text-emerald-600" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: true
      };

    case 'add-to-project':
      return {
        summary: action.summary,
        icon: <FolderInput size={24} className="text-indigo-700" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: false
      };
    default:
      return {
        summary: action.reason || 'Action was performed on this email.',
        icon: <Wrench size={24} className="text-stone-500" />,
        styles: 'bg-gray-50 border border-gray-100 shadow-sm rounded-lg animate-fade-in',
        reversible: false
      };
  }
};

const ActionDetailsModal = ({
  isOpen,
  onClose,
  action,
  email,
  labels,
  readOnlyMode,
  onRevert,
  onAcknowledge,
  revertLoading = false,
  acknowledgeLoading = false,
  onArchive,
  getEmailContent,
  provider,
  mailWritesDisabled = false
}) => {
  const modalRef = useRef(null);
  const providerName = getProviderDisplayName(provider);

  // Gather all actions taken on this email
  const actionsTaken = useMemo(() => {
    if (!email?.actions || !Array.isArray(email.actions)) {
      return action ? [action] : [];
    }
    // If action is not in email.actions, add it
    const found = email.actions.find(a => a.id === action?.id);
    if (!found && action) {
      return [action, ...email.actions];
    }
    return email.actions;
  }, [email, action]);

  // Find the index of the current action among assistant actions, default to 0
  const initialTab = useMemo(() => {
    if (!action) return 0;
    const assistantActions = actionsTaken.filter(a => a.actor === 'assistant');
    const idx = assistantActions.findIndex(a => a.id === action.id);
    return idx === -1 ? 0 : idx;
  }, [actionsTaken, action]);

  const [selectedActionIdx, setSelectedActionIdx] = useState(initialTab);
  const [isMaintenanceDraftModalOpen, setIsMaintenanceDraftModalOpen] = useState(false);
  const assistantActionCount = actionsTaken.filter(
    (candidateAction) => candidateAction.actor === 'assistant'
  ).length;

  // Keep selectedActionIdx in sync if actionsTaken or initialTab changes
  useEffect(() => {
    setSelectedActionIdx(initialTab);
  }, [initialTab, actionsTaken.length]);

  useEffect(() => {
    if (!isOpen) {
      setIsMaintenanceDraftModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    setIsMaintenanceDraftModalOpen(false);
  }, [selectedActionIdx]);

  // Handle case when actions are removed (e.g., through revert)
  useEffect(() => {
    if (assistantActionCount === 0) {
      // No assistant actions left, close the modal
      onClose();
      return;
    }

    // If current selected action no longer exists, reset to first available action
    if (selectedActionIdx >= assistantActionCount) {
      setSelectedActionIdx(0);
    }
  }, [assistantActionCount, selectedActionIdx, onClose]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMaintenanceDraftModalOpen) return;
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, onClose, isMaintenanceDraftModalOpen]);

  // Don't render if modal is closed or no assistant actions available
  const assistantActions = actionsTaken.filter(action => action.actor === 'assistant');
  if (!isOpen || assistantActions.length === 0) return null;

  const selectedAction = assistantActions[selectedActionIdx];
  const actionInfo = getActionConfig(selectedAction, labels);

  // Format date as "MMM D" (e.g., "Sep 30")
  const formatShortDate = (timestamp) => {
    if (!timestamp) return '';
    return dayjs(timestamp).format('MM/DD');
  };

  // Compose sender string
  const getSenderString = () => {
    if (!email) return '';
    const name = email.fromName || '';
    const address = email.from || '';
    return name
      ? `${name} <${address}>`
      : address;
  };

  // Compose subject
  const getSubject = () => {
    return email?.subject || '';
  };

  // Compose action summary (description)
  const getActionSummary = () => {
    // Prefer actionInfo.summary, fallback to action.reason or details
    if (actionInfo.summary) return actionInfo.summary;
    if (selectedAction.reason) return selectedAction.reason;
    if (selectedAction.details) return selectedAction.details;
    return '';
  };

  const getActionReply = () => {
    return (
      <>
        {email.actions?.filter(action => action.type === 'reply').slice(0, 1).map((action, actionIndex) => (
          <div key={getActionTypeKey(action, actionIndex)} className="flex flex-col gap-2">
            {Object.keys(action.metaData).filter(meta => meta === 'title').map((meta, metaIndex) => (
              <p key={getMetaKey(meta, metaIndex, action, actionIndex)} 
                className="text-sm text-gray-500 border-t border-gray-100 pt-2 px-3"
              >
                {action.metaData[meta]}
              </p>
            ))}

            {Object.keys(action.metaData).filter(meta => meta === 'content').map((meta, metaIndex) => (
              <p key={getReplyContentMetaKey(meta, metaIndex, action, actionIndex)} 
                className="text-base text-gray-500 flex items-start gap-4 w-full border border-gray-100 p-4 shadow-[inset_0_6px_8px_0_rgba(0,0,0,0.05)]"
           
           
              >
                {formatEmailText(action.metaData[meta])}
              </p>
            ))}
          </div>
        ))}
      </>
    )
  };

  const getActionForward = () => {
    return (
      <>
        {email.actions?.filter(action => action.type === 'forward').slice(0, 1).map((action, actionIndex) => (
          <div key={getActionTypeKey(action, actionIndex)} className="flex flex-col gap-2">
            {action.metaData?.subject && (
              <p className="text-sm text-gray-500 border-t border-gray-100 pt-2 px-3">
                {action.metaData.subject}
              </p>
            )}

            {action.metaData?.body && (
              <p className="text-base text-gray-500 flex items-start gap-4 w-full border border-gray-100 p-4 shadow-[inset_0_6px_8px_0_rgba(0,0,0,0.05)]">
                {formatEmailText(action.metaData.body)}
              </p>
            )}
          </div>
        ))}
      </>
    )
  };

  const getActionTaskCreated = () => {
    const taskId = selectedAction?.metaData?.taskId;
    const title = selectedAction?.metaData?.title;

    if (!title) {
      return null;
    }

    if (!taskId) {
      return (
        <p className="text-sm text-gray-500 border-t border-gray-100 pt-2 px-3">
          {title}
        </p>
      );
    }

    return (
      <Link
        to={`/task/${taskId}`}
        onClick={onClose}
        className="inline-block ml-0 text-left font-medium text-lg font-semibold text-sky-700 border-t border-gray-100 pt-2 px-3 rounded-lg hover:bg-sky-50 hover:text-sky-900 hover:underline underline-offset-2 transition-colors"
      >
        {title}
      </Link>
    );
  };

  const getActionAddToProject = () => {
    return (
      <>
        {email.actions?.filter(action => action.type === 'add-to-project').slice(0, 1).map((action, actionIndex) => (
          <div key={getActionTypeKey(action, actionIndex)} className="flex flex-col gap-2">
            {Object.keys(action.metaData).filter(meta => meta === 'title').map((meta, metaIndex) => (
            <p key={getMetaKey(meta, metaIndex, action, actionIndex)} className="text-sm text-gray-500 border-t border-gray-100 pt-2 px-3">
                {action.metaData[meta]}
              </p>
            ))}
          </div>
        ))}
      </>
    )
  };

  // Compose AI Reasoning
  const getAIReasoning = () => {
    return selectedAction.reason || '';
  };

  // Expand email button handler (stub)
  const handleExpandEmail = () => {
    getEmailContent(email?.id)
    onClose();
  };

  const handleArchiveEmail = () => {
    if (mailWritesDisabled || !onArchive) return;
    onArchive(email);
    onClose();
  };

  // --- Action Tabs UI ---
  const renderActionTabs = () => {
    return (
      <div className="flex items-center gap-2">
        {assistantActions.map((action, idx) => {
          const info = getActionConfig(action, labels);
          const label = formatActionTypeLabel(action, labels);
          const isActive = idx === selectedActionIdx;

          return (
            <div key={action.id || idx} className="flex items-center border-blue-100 border rounded-full">
              <button
                onClick={() => setSelectedActionIdx(idx)}
                className={`flex ml-0 items-center justify-center px-4 py-2 rounded-full transition-colors ${isActive
                  ? 'bg-white opacity-50 cursor-not-allowed hover:bg-white'
                  : 'bg-white text-gray-500 hover:bg-blue-50 hover:text-blue-700'
                }`}
                aria-label={label}
                type="button"
              >
                <span className="text-lg font-semibold">{info.icon}</span>
              </button>

              {isActive && (
                <div className="flex text-lg font-semibold items-center justify-center py-2 pr-4">
                  {label}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  // --- End Action Tabs UI ---

  return (
    <>
    <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-6">
      <div ref={modalRef} className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[75vh] flex flex-col">

        {/* Action Taken Tabs */}
        <div className="flex flex-col gap-4 px-8 pt-8 pb-0 mb-4">
          <div className="flex items-center justify-between gap-4">
            {/* Email Action Taken Icon Tabs */}
            {renderActionTabs()}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 bg-transparent hover:bg-gray-100 rounded-full p-1 transition-colors"
              aria-label="Close"
            >
              <X size={28} />
            </button>
          </div>
        </div>

        <div className="flex-grow border-t border-gray-100 overflow-y-auto p-8">
          {/* Email Info Card */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4 flex flex-col gap-2 border border-gray-100 shadow-sm">
            <div className="flex-1 min-w-0">
              <div
                className="text-2xl font-extrabold text-gray-900 leading-tight"
                title={getSubject()}
              >
                {getSubject()}
              </div>
              <div className="flex flex-row justify-between gap-3 mt-2 text-lg text-gray-600">
                <span className="truncate text-sm font-medium" title={getSenderString()}>
                  {getSenderString()}
                </span>
                <span className="text-sm font-semibold text-gray-500">
                  {formatShortDate(email?.date || email?.internalDate || email?.sentAt || email?.timestamp)}
                </span>
              </div>
            </div>
          </div>

          <div className="flex flex-row items-center justify-start gap-2 mb-8">
            <button
                onClick={handleExpandEmail}
                className="flex items-center gap-1 ml-0 px-3 py-1 text-sm font-medium rounded-lg border border-solid bg-gray-50 shadow-sm transition-colors
                text-slate-600 border-gray-100 hover:bg-gray-200"
              aria-label="Expand Email"
            >
              <Maximize2 size={16} /> View Email
            </button>
            {!mailWritesDisabled && onArchive && <button
              onClick={handleArchiveEmail}
              disabled={email?.archived}
              className={`flex items-center gap-1 ml-0 px-3 py-1 text-sm font-medium rounded-lg border border-solid bg-gray-50 shadow-sm transition-colors
                text-slate-600 border-gray-100 hover:bg-gray-200 ${email?.archived ? 'opacity-50 cursor-not-allowed' : ''}`}
              aria-label="Archive Email"
            >
              <Archive size={16} /> Archive Email
            </button>}
          </div>

          {/* Action Details Section */}
          <div className="flex flex-col gap-7 justify-start items-start">
            {/* Action Summary */}
            <div className="w-full flex flex-col">
              <div className="text-base text-gray-800 font-medium leading-relaxed px-2">
                {getActionSummary()}
              </div>
            </div>

            {/* Action Reply */}
            {selectedAction.type === 'reply' && getActionReply()}

            {/* Action Forward */}
            {selectedAction.type === 'forward' && getActionForward()}

            {/* Action Task Created */}
            {selectedAction.type === 'create-a-task' && getActionTaskCreated()}

            {/* Action Add to Project */}
            {selectedAction.type === 'add-to-project' && getActionAddToProject()}

            {/* Action Maintenance Ticket Created — title opens draft details modal */}
            {selectedAction.type === 'create-a-maintenance-ticket' &&
              selectedAction.metaData?.title && (
                <div className="flex items-start gap-4 w-full border rounded-lg border-gray-100 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-600 text-white">
                        <AlertTriangle className="size-3" />
                        {selectedAction.metaData.content.priority ? selectedAction.metaData.content.priority.charAt(0).toUpperCase() + selectedAction.metaData.content.priority.slice(1) : 'Urgent'}
                      </span>
                      <span className="text-xs text-gray-500">
                        {selectedAction.metaData.content.category
                          ? (selectedAction.metaData.content.category.charAt(0).toUpperCase() + selectedAction.metaData.content.category.slice(1) + ' Issue')
                          : 'Maintenance Issue'}
                      </span>
                    </div>

                    <button
                      type="button"
                      onClick={() => setIsMaintenanceDraftModalOpen(true)}
                      className="ml-0 text-left bg-transparent font-medium text-lg font-semibold text-gray-900 my-1 py-1 px-3 rounded-lg hover:no-underline hover:bg-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 transition-colors font-medium underline-offset-2 hover:underline"
                    >
                      {selectedAction.metaData.title}
                    </button>
                    <p className="text-sm text-gray-500 mt-1">
                      {selectedAction.metaData.content?.propertyName || 'Property'}{selectedAction.metaData.content?.unitLabel ? ` • Unit ${selectedAction.metaData.content.unitLabel}` : ''}
                    </p>
                  </div>
                </div>
              )}

            {/* AI Reasoning */}
            {getAIReasoning() && (
              <div className="w-full flex flex-col">
                <div className={`${actionInfo.styles} px-6 py-4 w-full shadow-sm`}>
                  <div className="text-sm text-slate-600 font-semibold mb-2">Reason behind this action</div>
                  <p className="text-base text-slate-900 leading-relaxed">{getAIReasoning()}</p>
                </div>
              </div>
            )}

            {/* Read Only Notice */}
            {readOnlyMode && (
              <div className="w-full flex flex-col">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-6 py-4 w-full shadow-sm">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-base font-semibold text-yellow-800">Read Only Mode</span>
                  </div>
                  <p className="text-base text-yellow-700 mt-1">
                    {`Actions were simulated. No actual changes were made to your ${providerName}.`}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions Acknowledgement */}
        <div className="border-t border-gray-100 px-10 py-6 flex gap-4 justify-end bg-white rounded-b-2xl">
          {actionInfo.reversible && onRevert && (
            <button
              onClick={() => onRevert(selectedAction)}
              disabled={revertLoading || acknowledgeLoading}
              className="flex items-center gap-2 px-6 py-3 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg font-semibold shadow-sm"
            >
              <Undo size={20} />
              {revertLoading ? 'Reverting...' : 'Undo Action'}
            </button>
          )}

          <button
            onClick={() => onAcknowledge(selectedAction)}
            disabled={revertLoading || acknowledgeLoading}
            className="flex items-center gap-2 px-6 py-3 bg-green-50 text-green-700 border border-green-200 rounded-xl hover:bg-green-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-lg font-semibold shadow-sm"
          >
            <ThumbsUp size={20} />
            {acknowledgeLoading ? 'Acknowledging...' : 'Acknowledge'}
          </button>
        </div>
      </div>
    </div>

    <MaintenanceTicketDraftModal
      isOpen={isMaintenanceDraftModalOpen}
      onClose={() => setIsMaintenanceDraftModalOpen(false)}
      metaData={selectedAction?.type === 'create-a-maintenance-ticket' ? selectedAction.metaData : null}
    />
    </>
  );
};

export default ActionDetailsModal;
